'use client';
import Image from 'next/image';
import { LocaleDate } from '@/components/locale-date';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { Article } from 'shared/src/types/article.type';
import { useState } from 'react';

interface BlogItemProps {
  locale: LocaleDetails;
  article: Article;
  main?: boolean;
}

export const BlogItem = ({ locale, article, main }: BlogItemProps) => {
  const [imageLoading, setImageLoading] = useState(true);

  if (main) {
    return (
      <div className="flex w-full flex-col overflow-hidden transition-all">
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-gray-50 dark:bg-gray-900/50">
          {imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500 dark:border-gray-700 dark:border-t-blue-400" />
            </div>
          )}
          <Image
            className="h-full w-full object-cover transition-all duration-500 group-hover:scale-[1.02]"
            src={article.thumbnail}
            alt={article.title}
            width={1200}
            height={675}
            quality={90}
            priority
            onLoad={() => setImageLoading(false)}
            onError={() => setImageLoading(false)}
          />
        </div>
        <div className="flex flex-col gap-3 pt-6">
          <div className="text-sm font-medium text-gray-500 dark:text-gray-400">
            <LocaleDate date={article.created} lang={locale} />
          </div>
          <h2 className="text-2xl font-semibold leading-tight text-gray-900 transition-colors group-hover:text-blue-600 dark:text-gray-50 dark:group-hover:text-blue-400 lg:text-3xl">
            {article.title}
          </h2>
          <p className="line-clamp-2 text-base leading-relaxed text-gray-600 dark:text-gray-400">
            {article.description}
          </p>
        </div>
      </div>
    );
  }

  // Regular horizontal card - improved version
  return (
    <div className="flex h-40 w-full gap-4 overflow-hidden rounded-xl transition-all hover:bg-gray-50 dark:hover:bg-gray-900/30 p-2 -m-2">
      <div className="relative h-full w-52 sm:w-60 flex-shrink-0 overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-900/50">
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-500 dark:border-gray-700 dark:border-t-blue-400" />
          </div>
        )}
        <Image
          className="h-full w-full object-cover aspect-video transition-all duration-500 group-hover:scale-[1.02]"
          src={article.thumbnail}
          alt={article.title}
          width={400}
          height={300}
          quality={90}
          onLoad={() => setImageLoading(false)}
          onError={() => setImageLoading(false)}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-2">
        {/* <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
          <LocaleDate date={article.created} lang={locale} />
        </div> */}
        <h3 className="line-clamp-2 text-lg font-semibold leading-snug text-gray-900 transition-colors group-hover:text-blue-600 dark:text-gray-50 dark:group-hover:text-blue-400">
          {article.title}
        </h3>
        <p className="line-clamp-3 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
          {article.description}
        </p>
      </div>
    </div>
  );
};
